import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loadProfileStatus } from '../services/profileStatus';
import { isPathBlockedForRole } from '../config/roleAccess';
import ChangePasswordGate from './ChangePasswordGate';

export default function ProtectedRoute({ children, requiredRole }) {
  const { user, userRole, loading, mustChangePassword } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);

  // Agents must finish their profile before using the rest of the system. Admins and the profile page are exempt.
  const needsProfileCheck = !!user && !mustChangePassword && userRole !== 'Admin' && location.pathname !== '/profile';

  useEffect(() => {
    if (!needsProfileCheck) return undefined;
    let active = true;
    loadProfileStatus(user.uid)
      .then((result) => { if (active) setStatus(result); })
      .catch((err) => {
        console.error('Could not check profile status:', err);
        if (active) setStatus({ missing: [] });
      });
    return () => { active = false; };
  }, [needsProfileCheck, user]);

  if (loading || (needsProfileCheck && !status)) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // First sign-in on the temporary password: nothing else until they set their own.
  if (mustChangePassword) return <ChangePasswordGate />;

  if (isPathBlockedForRole(userRole, location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  if (needsProfileCheck && status && status.missing.length > 0) {
    return <Navigate to="/profile" replace state={{ onboarding: true }} />;
  }

  return children;
}

const styles = {
  loadingContainer: {
    minHeight: '100vh',
    backgroundColor: '#000000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #333333',
    borderTop: '4px solid #D4AF37',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#999999'
  }
};
